/**
 * Polls DXF ingest jobs (rung 3, R3-A) for overlays whose companion DXF is
 * being parsed server-side.
 *
 * Much simpler than useOverlayBakePoller on purpose: there is no tileset, no
 * rollback lineage, and no status endpoint — the container writes its outputs
 * under an R2 prefix and finishes by writing done.json LAST (always, success
 * or failure). Completion polling is therefore just "does done.json exist
 * yet": a 404 means still running, a 200 carries the verdict. On success the
 * manifest is fetched once and its layer summary stamped into overlay.dxf so
 * the layer picker never needs a manifest round-trip on project open.
 *
 * Mounted once, next to useOverlayBakePoller in EditorPage.
 */

import { useEffect, useMemo, useRef } from 'react';
import { useProjectStore, useUIStore } from '../stores';
import { apiFetchRaw } from '../services/apiFetch';
import { fitDxfToBestLayout, layoutEntitySig, dxfAnchorEntities, REG_ALGO } from '../utils/dxfRegistration';
import type { DxfTextEntry, DxfRegistration } from '../utils/dxfRegistration';
import { pdfPageExtents } from '../utils/dxfRegistration';
import type { Overlay, OverlayDxf } from '../stores/projectStore';
import { effectiveCrop } from '../utils/planBake';

const POLL_INTERVAL = 5000; // ms — matches the other layer pollers

// How long to wait before we stop watching a job. The original 5 minutes was
// calibrated on Kfar (<10s for 159 layers / 6,618 texts) and that calibration
// was badly wrong: Hoshen 17Mwp is a >500MB DXF, and it overran the ceiling
// TWICE on 2026-08-30 while the container was still working. Both times the
// container went on to write `ok: true, 126 layers, 2533 texts` into a prefix
// nothing was reading any more, and both times the owner was told the server
// had failed and re-uploaded half a gigabyte for nothing.
//
// Sheet size has no useful upper bound, container starts are cold, and a poll
// is one cheap 404 — so the ceiling is now an hour, and reaching it no longer
// means "dead", only "we stopped watching". See recheckErroredIngests.
const MAX_POLLS = 720; // ≈ 60 minutes
const pollCounts = new Map<string, number>();

// Last time each job's done.json was re-checked after we stopped watching.
// This was a Set, which was a bug: stamping the stall mutates `overlays`, which
// trips the debounced sweep ~1.5s later, which spent the one and only re-check
// at the exact moment the job is BY DEFINITION still unfinished. Recovery then
// needed a full page reload to reset module state. A timestamp map lets the
// sweep try again periodically within a session.
const recheckedAt = new Map<string, number>();
const RECHECK_MIN_INTERVAL_MS = 60_000;
// Beyond this, a job that never produced a marker is not coming back, and
// re-checking it on every sweep is pointless. Jobs stamped before `startedAt`
// existed have no age and stay eligible — they are exactly the stranded ones.
const RECHECK_MAX_AGE_MS = 24 * 60 * 60 * 1000;

function isIngesting(o: Overlay): boolean {
  return o.dxf?.status === 'ingesting';
}

/** m:ss (or h:mm:ss past an hour) — the only progress figure this job can
 *  honestly report while the container is silent, so it is shared by the
 *  banner, the plan row and the completion toast. */
export function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

/** GET an ingest output through the ordinary asset route. 404 → null.
 *  Exported for the picker's "Align to layout" re-fit (PlansTicket). */
export async function fetchIngestJson<T>(outPrefix: string, name: string): Promise<T | null> {
  const res = await apiFetchRaw(`/api/v2/assets/${outPrefix}${name}`, { method: 'GET' });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`${name}: HTTP ${res.status}`);
  return await res.json() as T;
}

/**
 * Re-run the DXF→layout registration for an already-ingested overlay.
 *
 * Exists because the poller fits exactly ONCE, at ingest-ready — proven
 * insufficient on the very first production smoke: the vector layout had
 * been lost to a hard refresh (layouts persist only via explicit Save to
 * Server), so the fit ran against a layout-less site and correctly fell
 * back to the extents stretch. A layout imported AFTER the DXF could then
 * never bind without re-uploading 142MB. This fetches texts.json (~0.5MB)
 * and re-fits against whatever layouts exist NOW.
 *
 * Returns the new registration, or null when no layout matched.
 */
export async function refitDxfRegistration(
  overlay: Overlay,
): Promise<import('../utils/dxfRegistration').DxfLayoutRegistration | null> {
  const dxf = overlay.dxf;
  if (dxf?.status !== 'ready') return null;
  const textsDoc = await fetchIngestJson<{ texts: DxfTextEntry[] }>(dxf.outPrefix, 'texts.json');
  const data = useProjectStore.getState().data;
  const layouts = data.siteLayouts ?? (data.siteLayout ? [data.siteLayout] : []);
  if (!textsDoc?.texts?.length || !layouts.length) return null;
  const fit = fitDxfToBestLayout(textsDoc.texts, layouts);
  if (!fit) return null;
  // Guard against a mid-await re-attach swapping the job out from under us.
  const live = useProjectStore.getState().data.overlays.find((o) => o.id === overlay.id);
  if (!live || live.dxf?.jobId !== dxf.jobId) return null;
  useProjectStore.getState().updateOverlay(overlay.id, {
    dxf: { ...live.dxf!, registration: fit },
  });
  return fit;
}

interface DoneMarker {
  ok: boolean;
  jobId: string;
  error?: string;
}

interface Manifest {
  layers: Array<{ index: number; name: string; lineFeatures: number; fillFeatures?: number; textCount: number; overflow?: number }>;
  textTotal: number;
  extents: { minX: number; minY: number; maxX: number; maxY: number } | null;
  /** R3-D: set by pdf_ingest_worker. `page` is the displayed page box in PDF
   *  points (origin bottom-left, y up) — the exact frame the bake rasterizes,
   *  so the extents fallback can be made exact instead of "content bbox
   *  stretched onto the quad". */
  source?: 'pdf';
  page?: { index: number; widthPt: number; heightPt: number; rotate: number };
}

/**
 * Extents to stamp for the fallback registration. For a DXF: the manifest's
 * content bbox (rough by construction). For a PDF-derived ingest: the crop
 * window in page points — the overlay quad IS the cropped page (a crop moves
 * the corners), so stretching the crop window onto it maps every vector to
 * the pixel it was rasterized at. No crop = the whole page.
 */
export function ingestExtents(
  manifest: Manifest,
  bake: Overlay['bake'],
): { minX: number; minY: number; maxX: number; maxY: number } | undefined {
  if (manifest.source === 'pdf' && manifest.page) {
    const ext = pdfPageExtents(manifest.page, effectiveCrop(bake));
    if (ext) return ext;
  }
  return manifest.extents ?? undefined;
}

/**
 * Finish a job whose done.json reports success: read the manifest, fit the
 * registration, stamp the overlay. Shared by the live poller and the
 * after-the-fact recovery below so a recovered job lands in exactly the same
 * state as one that was watched the whole way through.
 *
 * Re-reads the live overlay before writing: the awaits are windows in which a
 * re-attach can swap jobId or a site switch can remove the overlay, and a
 * stale stamp must land on neither.
 */
async function completeIngest(overlayId: string, overlayName: string, dxf: OverlayDxf): Promise<void> {
  // done.json is written AFTER manifest.json, so this cannot race the worker;
  // a missing manifest here is a real error.
  const manifest = await fetchIngestJson<Manifest>(dxf.outPrefix, 'manifest.json');
  if (!manifest) throw new Error('manifest.json missing after done.json');

  // Registration, fitted ONCE here and persisted: match the DXF's text against
  // the site's layout labels (unique labels on both sides = anchor pairs;
  // similarity fit; see dxfRegistration.ts). Falls back to an extents stretch
  // onto the plan quad when there is no layout to match — rough, but the
  // linework lands on screen.
  let registration: DxfRegistration = { kind: 'extents' };
  try {
    const textsDoc = await fetchIngestJson<{ texts: DxfTextEntry[] }>(dxf.outPrefix, 'texts.json');
    const data = useProjectStore.getState().data;
    const layouts = data.siteLayouts ?? (data.siteLayout ? [data.siteLayout] : []);
    if (textsDoc?.texts?.length && layouts.length) {
      const fit = fitDxfToBestLayout(textsDoc.texts, layouts);
      if (fit) registration = fit;
    }
  } catch (fitErr) {
    console.warn('[DxfPoller] registration fit failed, using extents fallback:', fitErr);
  }

  const live = useProjectStore.getState().data.overlays.find((o) => o.id === overlayId);
  if (!live || live.dxf?.jobId !== dxf.jobId) return;
  useProjectStore.getState().updateOverlay(overlayId, {
    dxf: {
      ...dxf,
      status: 'ready',
      error: undefined,
      stalled: undefined,
      layers: manifest.layers.map((l) => ({
        index: l.index, name: l.name,
        lineFeatures: l.lineFeatures, fillFeatures: l.fillFeatures,
        textCount: l.textCount,
        // Features the ingest dropped at the per-layer cap — the picker shows
        // "N of M" so a half-drawn module layer is not mistaken for a bad
        // extraction (until vector tiles remove the cap).
        ...(l.overflow ? { overflow: l.overflow } : {}),
      })),
      textTotal: manifest.textTotal,
      extents: ingestExtents(manifest, live.bake),
      registration,
    },
  });
  console.log(
    `[DxfPoller] ${overlayName}: ingest ready (${manifest.layers.length} layers, `
    + `${manifest.textTotal} texts; registration=${registration.kind}`
    + (registration.kind === 'layout' ? ` ${registration.matched} anchors rms ${registration.rmsPt}pt)` : ')'),
  );
  // Completion used to be a console line and a chip flipping to "DXF ✓" — the
  // one moment in the whole job with genuinely precise numbers to report, spent
  // in silence. The counts come straight from the manifest; the duration is
  // wall-clock from the dispatch stamp (absent on pre-startedAt jobs).
  const elapsed = dxf.startedAt ? formatElapsed(Date.now() - Date.parse(dxf.startedAt)) : null;
  useUIStore.getState().addNotification({
    type: 'success',
    title: manifest.source === 'pdf' ? 'PDF vectors ready' : 'DXF ready',
    message: `${overlayName} — ${manifest.layers.length} layers, `
      + `${manifest.textTotal.toLocaleString()} texts`
      + (elapsed ? ` in ${elapsed}` : '')
      + '. Pick the layers to draw from the plan row.',
  });
}

/**
 * Recover jobs we gave up on that finished anyway.
 *
 * "Timed out" is a statement about how long WE waited, not about the server:
 * the container has no way to tell us it is still working, it just writes
 * done.json when it lands. Hoshen 17Mwp proved the difference expensive — a
 * complete, successful ingest sat in R2 while the overlay said `error` and the
 * only offered remedy was re-uploading the DXF, which for these files is
 * hundreds of megabytes. So before believing our own timeout, look again.
 *
 * Also upgrades a client-side timeout to the server's real error message when
 * the job did finish and failed — a more useful thing to show than "timed out".
 *
 * Returns the number of overlays recovered.
 */
export async function recheckErroredIngests(opts?: { force?: boolean }): Promise<number> {
  const overlays = useProjectStore.getState().data.overlays;
  const now = Date.now();
  let recovered = 0;
  for (const overlay of overlays) {
    const dxf = overlay.dxf;
    if (dxf?.status !== 'error' || !dxf.outPrefix || !dxf.jobId) continue;
    if (!opts?.force) {
      const last = recheckedAt.get(dxf.jobId);
      if (last !== undefined && now - last < RECHECK_MIN_INTERVAL_MS) continue;
      const started = dxf.startedAt ? Date.parse(dxf.startedAt) : NaN;
      if (!Number.isNaN(started) && now - started > RECHECK_MAX_AGE_MS) continue;
    }
    recheckedAt.set(dxf.jobId, now);
    try {
      const done = await fetchIngestJson<DoneMarker>(dxf.outPrefix, 'done.json');
      if (!done) continue; // genuinely never finished — leave the error standing
      if (!done.ok) {
        const live = useProjectStore.getState().data.overlays.find((o) => o.id === overlay.id);
        if (live?.dxf?.jobId !== dxf.jobId) continue;
        useProjectStore.getState().updateOverlay(overlay.id, {
          // Clearing `stalled` is the point of this branch: we now KNOW the job
          // finished and failed, so it is no longer "we stopped watching". Left
          // set, the banner would go on offering "Check now" and telling the
          // user it may still be running, about a job proven dead.
          dxf: { ...dxf, status: 'error', stalled: undefined, error: done.error ?? 'Unknown ingest error' },
        });
        continue;
      }
      await completeIngest(overlay.id, overlay.name, dxf);
      recovered++;
    } catch (err) {
      console.warn('[DxfPoller] re-check of a timed-out ingest failed:', (err as Error).message);
    }
  }
  return recovered;
}

// ── Registration staleness auto-heal ────────────────────────────────────────
// The fit is a snapshot of the label set; Kalia proved it silently rots when
// labels change after alignment (re-imports, Fix/Undo churn) — labels
// rendered live while the DXF rode a 1.5-5m-stale frame from the SAME
// anchors. When a registration's entitySig no longer matches its layout's
// labels (or is absent — legacy fits), re-fit automatically. A sig we
// already attempted and failed on is not retried (loop guard).
const healAttempted = new Map<string, string>();

function healStaleRegistrations(notify: (msg: string) => void): void {
  const st = useProjectStore.getState();
  const layouts = st.data.siteLayouts ?? (st.data.siteLayout ? [st.data.siteLayout] : []);
  for (const overlay of st.data.overlays) {
    const reg = overlay.dxf?.registration;
    if (overlay.dxf?.status !== 'ready' || reg?.kind !== 'layout') continue;
    const layout = layouts.find((l) => l.id === reg.layoutId);
    if (!layout) continue; // dangling — the picker warns; Re-align rebinds
    // Hand-moved labels are invisible to the registration (anchor set AND
    // signature) — dragging labels no longer counts as "labels changed"
    // once they carry the manuallyMoved flag.
    const sig = layoutEntitySig(dxfAnchorEntities(layout.textEntities));
    if (reg.entitySig === sig && reg.algo === REG_ALGO) continue;
    const attemptKey = `${sig}:${REG_ALGO}`;
    if (healAttempted.get(overlay.id) === attemptKey) continue;
    healAttempted.set(overlay.id, attemptKey);
    const before = reg.matrix;
    void refitDxfRegistration(overlay).then((fit) => {
      if (!fit) { console.warn('[DxfHeal] stale registration re-fit found no match', overlay.id); return; }
      // Toast only when the alignment MOVED materially. A label's first
      // hand-move re-fits with it dropped from the anchors — the result is
      // near-identical, and a "re-aligned" toast for an invisible change
      // reads as the DXF chasing the labels (owner report 2026-09-03).
      const moved =
        Math.abs(fit.matrix[2] - before[2]) > 0.5
        || Math.abs(fit.matrix[5] - before[5]) > 0.5
        || Math.abs(fit.matrix[0] - before[0]) > 1e-4
        || Math.abs(fit.matrix[1] - before[1]) > 1e-4
        || Math.abs(fit.matrix[3] - before[3]) > 1e-4
        || Math.abs(fit.matrix[4] - before[4]) > 1e-4;
      if (moved) notify(`DXF re-aligned after label changes — ${fit.matched} labels (±${fit.rmsPt} pt).`);
    }).catch((err) => console.warn('[DxfHeal] re-fit failed:', err));
  }
}

export default function useDxfIngestPoller() {
  const siteId = useProjectStore((s) => s.meta.siteId);
  const overlays = useProjectStore((s) => s.data.overlays);
  const siteLayouts = useProjectStore((s) => s.data.siteLayouts);
  const siteLayoutAlias = useProjectStore((s) => s.data.siteLayout);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const healTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced staleness sweep on any layout/overlay change (and on load).
  useEffect(() => {
    if (!siteId) return;
    if (healTimerRef.current) clearTimeout(healTimerRef.current);
    healTimerRef.current = setTimeout(() => {
      healStaleRegistrations((message) => {
        useUIStore.getState().addNotification({ type: 'info', title: 'DXF alignment refreshed', message });
      });
      // Same sweep, second job: a job this browser gave up on may have
      // finished on the server after we stopped listening. Opening the
      // project is when that gets noticed.
      void recheckErroredIngests().then((n) => {
        if (n > 0) {
          useUIStore.getState().addNotification({
            type: 'success',
            title: 'DXF ingest recovered',
            message: `${n} DXF ${n === 1 ? 'layer set' : 'layer sets'} finished on the server after the client stopped waiting — loaded now, no re-upload needed.`,
          });
        }
      });
    }, 1500);
    return () => {
      if (healTimerRef.current) clearTimeout(healTimerRef.current);
    };
  }, [siteId, overlays, siteLayouts, siteLayoutAlias]);

  // Key off the ingesting SET so unrelated overlay re-renders (opacity drags)
  // can't restart the interval — same construction as useOverlayBakePoller.
  const pendingKey = useMemo(
    () => overlays.filter(isIngesting).map((o) => o.dxf!.jobId).sort().join(','),
    [overlays],
  );

  useEffect(() => {
    const stop = () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };

    if (!siteId || !pendingKey) {
      stop();
      return;
    }

    const poll = async () => {
      const state = useProjectStore.getState();
      if (!state.meta.siteId) return;
      const stillIngesting = state.data.overlays.filter(isIngesting);
      if (stillIngesting.length === 0) {
        stop();
        return;
      }

      for (const overlay of stillIngesting) {
        const dxf = overlay.dxf!;
        const jobId = dxf.jobId;
        // Every write re-reads the LIVE overlay first: the awaits below are
        // windows where a re-attach can swap jobId or a site switch can
        // remove the overlay, and a stale stamp must not land on either.
        const liveOverlay = () => {
          const cur = useProjectStore.getState().data.overlays
            .find((o) => o.id === overlay.id);
          return cur && cur.dxf?.jobId === jobId ? cur : null;
        };
        const stamp = (next: OverlayDxf) => {
          const live = liveOverlay();
          if (!live) return;
          useProjectStore.getState().updateOverlay(overlay.id, { dxf: next });
        };

        try {
          const done = await fetchIngestJson<DoneMarker>(dxf.outPrefix, 'done.json');
          if (!done) {
            const n = (pollCounts.get(jobId) ?? 0) + 1;
            pollCounts.set(jobId, n);
            if (n >= MAX_POLLS) {
              // NOT "it failed" — we have no evidence of that, and telling the
              // user to re-attach cost two 500MB re-uploads of a file that had
              // parsed correctly both times. Say what is actually known.
              stamp({
                ...dxf,
                status: 'error',
                stalled: true,
                error: 'Still no result after an hour. The job may yet be running — reopen the project to pick it up. Do not re-upload.',
              });
              useUIStore.getState().addNotification({
                type: 'warning',
                title: 'Still waiting on the DXF',
                message: `${overlay.name} — the server has not answered in an hour. It may still be working; reopening the project will load the result if it lands. No need to upload the file again.`,
              });
            }
            continue;
          }
          pollCounts.delete(jobId);

          if (!done.ok) {
            stamp({ ...dxf, status: 'error', error: done.error ?? 'Unknown ingest error' });
            useUIStore.getState().addNotification({
              type: 'error',
              title: 'DXF ingest failed',
              message: `${overlay.name} — ${done.error ?? 'unknown error'}`,
            });
            continue;
          }

          await completeIngest(overlay.id, overlay.name, dxf);
        } catch (err) {
          // Transient (network, 401 mid-refresh, 5xx) — keep polling; the
          // MAX_POLLS ceiling still bounds the total wait.
          const n = (pollCounts.get(jobId) ?? 0) + 1;
          pollCounts.set(jobId, n);
          console.warn('[DxfPoller] poll failed:', (err as Error).message);
          if (n >= MAX_POLLS) {
            stamp({
              ...dxf,
              status: 'error',
              error: 'Could not reach the ingest status. Reopen the project to pick up the result. Do not re-upload.',
            });
          }
        }
      }
    };

    poll();
    timerRef.current = setInterval(poll, POLL_INTERVAL);

    return stop;
  }, [siteId, pendingKey]);
}
